import { useParams, Link, useNavigate } from "react-router-dom";
import { logger } from "../logger";
import {
  useProjects,
  useUpdateProject,
  useDeleteProject,
  useCreateActionItem,
  useUpdateActionItem,
  useDeleteActionItem,
} from "../hooks/useData";
import { SingleProjectView, VoiceCaptureSystem } from "../components_v2";
import type { ProjectType } from "../components_v2";
import type { ModificationResult } from "../model";

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: projects = [], isLoading } = useProjects();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const createActionItem = useCreateActionItem();
  const updateActionItem = useUpdateActionItem();
  const deleteActionItem = useDeleteActionItem();

  const project = projects.find((p) => p.id === parseInt(id!));

  const handleModificationsApproved = async (
    modifications: ModificationResult,
  ) => {
    try {
      // Apply project modifications
      for (const projectMod of modifications.projectModifications) {
        if (projectMod.operation === "update" && projectMod.id) {
          await updateProject.mutateAsync({
            id: projectMod.id,
            name: projectMod.name,
            desiredOutcome: projectMod.desiredOutcome,
          });
        } else if (projectMod.operation === "delete" && projectMod.id) {
          await deleteProject.mutateAsync(projectMod.id);
          navigate("/");
        }
      }

      // Apply action item modifications
      for (const actionMod of modifications.actionItemModifications) {
        if (actionMod.operation === "create") {
          await createActionItem.mutateAsync({
            content: actionMod.content || "New Action Item",
            projectId: parseInt(id!),
            status: "actionable",
          });
        } else if (actionMod.operation === "update" && actionMod.id) {
          await updateActionItem.mutateAsync({
            id: actionMod.id,
            content: actionMod.content,
          });
        } else if (actionMod.operation === "delete" && actionMod.id) {
          await deleteActionItem.mutateAsync({
            id: actionMod.id,
            projectId: parseInt(id!),
          });
        }
      }
    } catch (error) {
      logger.error("Failed to apply modifications", { error });
    }
  };

  const handleUpdateProject = async (updatedProject: ProjectType) => {
    await updateProject.mutateAsync({
      id: updatedProject.id,
      name: updatedProject.name,
      desiredOutcome: updatedProject.desiredOutcome,
    });
  };

  // Convert project to v2 format
  const v2Project: ProjectType | undefined = project
    ? {
        id: project.id,
        name: project.name,
        desiredOutcome: project.desiredOutcome,
        actionItems:
          project.actionItems?.map((item) => ({
            id: Number(item.id),
            content: item.content,
            status: item.status || "actionable",
            date: item.date,
            delegatedTo: item.delegatedTo,
          })) || [],
      }
    : undefined;

  if (isLoading) {
    return (
      <main className="font-sans m-8">
        <div>Loading...</div>
      </main>
    );
  }

  if (!project || !v2Project) {
    return (
      <main className="font-sans m-8">
        <div className="mb-4">
          <Link to="/" className="text-blue-600 hover:underline">
            ← Back to Home
          </Link>
        </div>
        <div>Project not found</div>
      </main>
    );
  }

  return (
    <main className="font-sans m-8">
      <div className="mb-4">
        <Link to="/" className="text-blue-600 hover:underline">
          ← Back to Home
        </Link>
      </div>

      {/* V2 Single Project View */}
      <SingleProjectView
        project={v2Project}
        onUpdateProject={handleUpdateProject}
      />

      {/* V2 Voice Capture System for Project-Specific Mode */}
      <VoiceCaptureSystem
        projectId={parseInt(id!)}
        onModificationsApproved={handleModificationsApproved}
      />
    </main>
  );
}
